import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import { differenceInDays, format, isAfter, parseISO, startOfDay } from 'date-fns';
import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { Link, Navigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileFAB from '../components/MobileFAB';
import ResponsiveChart from '../components/ResponsiveChart';
import ResponsiveTable from '../components/ResponsiveTable';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isPlatformOwner } from '../utils/authUser';

// Import the Column type from ResponsiveTable
interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
  mobileLabel?: string;
  mobilePriority?: number;
  className?: string;
  type?: 'text' | 'status' | 'date' | 'number' | 'link' | 'action';
}

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title
);

// SVG Icons - Refined minimal design
const LeafIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const WineGlassIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const WarehouseIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const WineBottleIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const FlaskIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

interface WineBatch {
  id: number;
  batch_id: string;
  batchId?: string;
  batch_name: string;
  batchName?: string;
  wine_type: string;
  wineType?: string;
  startDate?: string;
  start_date?: string;
  status: string;
}

interface Vineyard {
  id: number;
  name: string;
  location: string;
  plantedVariety: string;
  acreage: number;
}

interface Vessel {
  id: number;
  vesselId?: string;
  vessel_id?: string;
  vesselType?: string;
  type?: string;
  capacity: number;
  capacityUnit?: string;
  capacity_unit?: string;
  material: string;
  status: string;
}

interface FermentationProcess {
  id: number;
  batch_id: number;
  vessel_id: number;
  startDate: string;
  status: string;
  batch?: {
    batch_id: string;
    batch_name: string;
  };
  vessel?: {
    vessel_id: string;
  };
}

interface AgingProcess {
  id: number;
  batch_id: number;
  vessel_id: number;
  startDate: string;
  status: string;
  batch?: {
    batch_id: string;
    batch_name: string;
  };
  vessel?: {
    vessel_id: string;
  };
}

interface BottlingRun {
  id: number;
  batch_id: number;
  batchId?: number;
  bottlingDate?: string;
  bottling_date?: string;
  bottleCount?: number;
  bottle_count?: number;
  bottleSize?: string;
  bottle_size?: string;
  status: string;
  batch?: {
    batch_id?: string;
    batchId?: string;
    batch_name?: string;
    batchName?: string;
  };
}

// Helper function for safe date formatting
const safeFormatDate = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  try {
    const parsedDate = parseISO(value);
    if (isNaN(parsedDate.getTime())) return 'Invalid Date';
    return format(parsedDate, 'MMM d, yyyy');
  } catch (error) {
    console.error('Date formatting error:', error, 'Value:', value);
    return 'Invalid Date';
  }
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [batches, setBatches] = useState<WineBatch[]>([]);
  const [vineyards, setVineyards] = useState<Vineyard[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [fermentations, setFermentations] = useState<FermentationProcess[]>([]);
  const [agingProcesses, setAgingProcesses] = useState<AgingProcess[]>([]);
  const [bottlingRuns, setBottlingRuns] = useState<BottlingRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'production' | 'quality'>('overview');

  useEffect(() => {
    if (isPlatformOwner(user)) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch data from all sections in parallel
        const [
          batchesRes,
          vineyardsRes,
          vesselsRes,
          fermentationsRes,
          agingRes,
          bottlingRes
        ] = await Promise.all([
          api.get('/api/batch'),
          api.get('/api/vineyard'),
          api.get('/api/vessel'),
          api.get('/api/fermentation'),
          api.get('/api/aging'),
          api.get('/api/bottling')
        ]);

        setBatches(batchesRes.data);
        setVineyards(vineyardsRes.data);
        setVessels(vesselsRes.data);
        setFermentations(fermentationsRes.data);
        setAgingProcesses(agingRes.data);
        setBottlingRuns(bottlingRes.data);
      } catch (err: any) {
        console.error('Dashboard data fetch error:', err);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load dashboard';
        setError(typeof message === 'string' ? message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (isPlatformOwner(user)) {
    return <Navigate to="/backoffice" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
        <p className="ml-2 text-monday-text-muted">Loading your dashboard...</p>
      </div>
    );
  }

  // Helper functions to look up batch and vessel names
  const getBatchName = (batchId: number): string => {
    const batch = batches.find(b => b.id === batchId);
    return batch?.batchName || batch?.batch_name || `Batch #${batchId}`;
  };

  const getVesselId = (vesselId: number): string => {
    const vessel = vessels.find(v => v.id === vesselId);
    return vessel?.vesselId || vessel?.vessel_id || `Vessel #${vesselId}`;
  };

  // Summary stats calculations
  const activeFermentations = fermentations.filter(f => f.status === 'Active').length;
  const activeAgingProcesses = agingProcesses.filter(a => a.status === 'Active').length;
  const plannedBottlings = bottlingRuns.filter(b => b.status === 'Planned').length;
  const availableVessels = vessels.filter(v => v.status === 'Empty').length;
  const inUseVessels = vessels.filter(v => v.status === 'In Use').length;

  const totalAcreage = vineyards.reduce((sum, vineyard) => {
    // Make sure vineyard.acreage is a number
    const acreage = typeof vineyard.acreage === 'number' ? vineyard.acreage : 0;
    return sum + acreage;
  }, 0);
  const totalBottles = bottlingRuns.reduce((sum, run) => sum + (run.bottleCount || run.bottle_count || 0), 0);

  // Wine type distribution for charts - handle both camelCase and snake_case
  const wineTypes = batches.reduce((acc: Record<string, number>, batch) => {
    const wineType = batch.wineType || batch.wine_type || 'Unknown';
    acc[wineType] = (acc[wineType] || 0) + 1;
    return acc;
  }, {});

  // Vessel utilization percentage
  const vesselUtilizationPercent = vessels.length > 0
    ? Math.round((inUseVessels / vessels.length) * 100)
    : 0;

  // Upcoming bottling dates
  const upcomingBottlingRuns = bottlingRuns
    .filter(run => {
      if (!run.bottlingDate) return false;
      try {
        const parsedDate = parseISO(run.bottlingDate);
        return !isNaN(parsedDate.getTime()) && isAfter(parsedDate, startOfDay(new Date()));
      } catch (error) {
        console.error('Date parsing error in filter:', error, 'Value:', run.bottlingDate);
        return false;
      }
    })
    .sort((a, b) => {
      try {
        const dateStrA = a.bottlingDate || a.bottling_date || '';
        const dateStrB = b.bottlingDate || b.bottling_date || '';
        if (!dateStrA || !dateStrB) return 0;
        const dateA = parseISO(dateStrA);
        const dateB = parseISO(dateStrB);
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
        return dateA.getTime() - dateB.getTime();
      } catch (error) {
        console.error('Date parsing error in sort:', error);
        return 0;
      }
    })
    .slice(0, 5);

  // Chart data for wine types
  const wineTypeChartData = {
    labels: Object.keys(wineTypes),
    datasets: [
      {
        data: Object.values(wineTypes),
        backgroundColor: ['#B4233A', '#B77A2A', '#3F8A4E', '#3F718A', '#DCA34A', '#5DA56C', '#C73340'],
        borderWidth: 1,
      },
    ],
  };

  // Production timeline data
  const productionStages = {
    fermentation: activeFermentations,
    aging: activeAgingProcesses,
    bottling: plannedBottlings,
  };

  const productionChartData = {
    labels: ['Fermentation', 'Aging', 'Bottling'],
    datasets: [
      {
        label: 'Active Processes',
        data: [productionStages.fermentation, productionStages.aging, productionStages.bottling],
        backgroundColor: ['#B4233A', '#B77A2A', '#3F8A4E'],
      },
    ],
  };

  // Table columns for responsive tables with improved mobile experience
  const batchColumns: Column[] = [
    {
      key: 'batch_id',
      label: 'Batch ID',
      mobilePriority: 3,
      type: 'link',
      render: (value: string, row: any) => (
        <Link to={`/batches/${row.id}`} className="text-monday-text hover:text-monday-text font-medium">
          {row.batchId || row.batch_id || value || 'N/A'}
        </Link>
      )
    },
    {
      key: 'batch_name',
      label: 'Name',
      mobilePriority: 2,
      type: 'text',
      render: (value: string, row: any) => row.batchName || row.batch_name || value || 'N/A'
    },
    {
      key: 'wine_type',
      label: 'Type',
      mobilePriority: 1,
      type: 'text',
      render: (value: string, row: any) => row.wineType || row.wine_type || value || 'N/A'
    },
    {
      key: 'status',
      label: 'Status',
      mobilePriority: 2,
      type: 'status'
    },
  ];

  const bottlingColumns: Column[] = [
    {
      key: 'batch_name',
      label: 'Batch',
      mobilePriority: 3,
      type: 'text',
      render: (value: string, row: any) =>
        row.batch?.batch_name || row.batch?.batchName || getBatchName(row.batch_id || row.batchId)
    },
    {
      key: 'bottlingDate',
      label: 'Date',
      mobilePriority: 2,
      type: 'date',
      render: (value: string, row: any) => safeFormatDate(value || row.bottling_date)
    },
    {
      key: 'bottleCount',
      label: 'Bottles',
      mobilePriority: 1,
      type: 'number',
      render: (value: number, row: any) => {
        const count = value || row.bottle_count || 0;
        return count.toLocaleString();
      }
    },
    {
      key: 'bottleSize',
      label: 'Size',
      mobilePriority: 1,
      type: 'text',
      render: (value: string, row: any) => value || row.bottle_size || 'N/A'
    },
  ];

  const fermentationColumns: Column[] = [
    {
      key: 'batch_name',
      label: 'Batch',
      mobilePriority: 3,
      type: 'text',
      render: (value: string, row: any) =>
        row.batch?.batch_name || row.batch?.batchName || getBatchName(row.batch_id || row.batchId)
    },
    {
      key: 'vessel_id',
      label: 'Vessel',
      mobilePriority: 2,
      type: 'text',
      render: (value: string, row: any) =>
        row.vessel?.vessel_id || row.vessel?.vesselId || getVesselId(row.vessel_id || row.vesselId || value)
    },
    {
      key: 'startDate',
      label: 'Start Date',
      mobilePriority: 1,
      type: 'date',
      render: (value: string, row: any) => safeFormatDate(value || row.start_date)
    },
    {
      key: 'status',
      label: 'Status',
      mobilePriority: 2,
      type: 'status'
    },
    {
      key: 'actions',
      label: 'Actions',
      mobilePriority: 0,
      type: 'action',
      render: (value: string, row: any) => (
        <Link
          to={`/fermentation/${row.id}`}
          className="text-monday-text hover:text-monday-text transition-colors duration-200 font-medium"
        >
          Details
        </Link>
      )
    },
  ];

  const vesselColumns: Column[] = [
    {
      key: 'vesselId',
      label: 'Vessel ID',
      mobilePriority: 3,
      type: 'link',
      render: (value: string, row: any) => (
        <Link to={`/vessels/${row.id}`} className="text-monday-text hover:text-monday-text font-medium">
          {row.vesselId || row.vessel_id || value || 'N/A'}
        </Link>
      )
    },
    {
      key: 'vesselType',
      label: 'Type',
      mobilePriority: 2,
      type: 'text',
      render: (value: string, row: any) => row.vesselType || row.type || value || 'N/A'
    },
    {
      key: 'capacity',
      label: 'Capacity',
      mobilePriority: 1,
      type: 'number',
      render: (value: number, row: any) => {
        const cap = value || row.capacity || 0;
        const unit = row.capacityUnit || row.capacity_unit || 'L';
        return `${cap} ${unit}`;
      }
    },
    {
      key: 'material',
      label: 'Material',
      mobilePriority: 1,
      type: 'text'
    },
    {
      key: 'status',
      label: 'Status',
      mobilePriority: 2,
      type: 'status'
    },
  ];

  // Mobile FAB actions
  const fabActions = [
    {
      id: 'new-batch',
      label: 'New Wine Batch',
      icon: FiIcons.FiDroplet({ size: 20 }),
      href: '/batches',
      color: 'bg-green-600'
    },
    {
      id: 'new-vineyard',
      label: 'Add Vineyard',
      icon: FiIcons.FiMapPin({ size: 20 }),
      href: '/vineyards',
      color: 'bg-primary-500'
    },
    {
      id: 'new-vessel',
      label: 'Add Vessel',
      icon: FiIcons.FiDatabase({ size: 20 }),
      href: '/vessels',
      color: 'bg-amber-600'
    },
    {
      id: 'tracking',
      label: 'View Tracking',
      icon: FiIcons.FiTrendingUp({ size: 20 }),
      href: '/tools/tracking',
      color: 'bg-purple-600'
    }
  ];

  const mainFabAction = {
    id: 'quick-actions',
    label: 'Quick Actions',
    icon: FiIcons.FiPlus({ size: 24 })
  };

  return (
    <div className="min-h-screen mobile-safe-area">
      <div className="max-w-[1500px] mx-auto py-2 md:py-3">
        {/* Dashboard Header - Enhanced */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1.5 h-9 bg-gradient-to-b from-primary-500 via-secondary-400 to-success-500 rounded-full"></div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-monday-text tracking-tight">
                  Welcome back, {user?.firstName || 'Winemaker'}
                </h1>
              </div>
              <div className="flex items-center gap-2 ml-5">
                <svg className="w-4 h-4 text-monday-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-monday-text-muted text-sm md:text-base">
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex items-center gap-3">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-violet-400 to-blue-400 rounded-lg opacity-0 group-hover:opacity-10 transition-opacity blur"></div>
                <div className="relative bg-gradient-to-br from-white to-monday-header border border-monday-border-light rounded-2xl px-5 py-3 group-hover:border-primary-200 transition-all duration-300 shadow-xs">
                  <div className="text-xs font-semibold text-monday-text-muted uppercase tracking-wider mb-1">Total Batches</div>
                  <div className="text-3xl font-extrabold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">{batches.length}</div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard Navigation Tabs - Enhanced */}
          <div className="border-b-2 border-monday-border-light">
            <nav className="-mb-0.5 flex space-x-2 md:space-x-3 overflow-x-auto mobile-scrollbar">
              <button
                onClick={() => setActiveTab('overview')}
                className={`mobile-nav-item mobile-touch-target group relative ${activeTab === 'overview'
                  ? 'text-monday-text'
                  : 'text-monday-text-muted hover:text-monday-text'
                  } whitespace-nowrap pb-3 px-4 font-medium text-sm transition-all duration-200`}
              >
                <span className="relative z-10">Overview</span>
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all duration-300 ${activeTab === 'overview'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                  : 'bg-transparent group-hover:bg-monday-hover'
                  }`}></div>
              </button>
              <button
                onClick={() => setActiveTab('production')}
                className={`mobile-nav-item mobile-touch-target group relative ${activeTab === 'production'
                  ? 'text-monday-text'
                  : 'text-monday-text-muted hover:text-monday-text'
                  } whitespace-nowrap pb-3 px-4 font-medium text-sm transition-all duration-200`}
              >
                <span className="relative z-10">Production Pipeline</span>
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all duration-300 ${activeTab === 'production'
                  ? 'bg-gradient-to-r from-secondary-500 to-primary-500'
                  : 'bg-transparent group-hover:bg-monday-hover'
                  }`}></div>
              </button>
              <button
                onClick={() => setActiveTab('quality')}
                className={`mobile-nav-item mobile-touch-target group relative ${activeTab === 'quality'
                  ? 'text-monday-text'
                  : 'text-monday-text-muted hover:text-monday-text'
                  } whitespace-nowrap pb-3 px-4 font-medium text-sm transition-all duration-200`}
              >
                <span className="relative z-10">Quality & Alerts</span>
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all duration-300 ${activeTab === 'quality'
                  ? 'bg-gradient-to-r from-success-500 to-accent-500'
                  : 'bg-transparent group-hover:bg-monday-hover'
                  }`}></div>
              </button>
            </nav>
          </div>
        </div>

        {/* Render different content based on active tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Key Metrics - Enhanced UX Design */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
              {/* Vineyard Stats */}
              <Link to="/vineyards" className="group relative bg-gradient-to-br from-white to-success-50/40 border border-monday-border-light rounded-xl p-5 hover:border-success-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/0 to-emerald-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2.5 bg-emerald-100 rounded-lg text-emerald-600 group-hover:scale-110 transition-transform duration-300">
                      <LeafIcon />
                    </div>
                    <div className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Active</div>
                  </div>
                  <p className="text-sm font-medium text-monday-text-muted mb-1">Vineyards</p>
                  <p className="text-3xl font-bold text-monday-text mb-2 group-hover:text-emerald-600 transition-colors">{vineyards.length}</p>
                  <p className="text-sm text-monday-text-muted">
                    {totalAcreage.toFixed(1)} acres total
                  </p>
                </div>
              </Link>

              {/* Production Stats */}
              <Link to="/batches" className="group relative bg-gradient-to-br from-white to-primary-50/40 border border-monday-border-light rounded-xl p-5 hover:border-primary-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-400/0 to-violet-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2.5 bg-violet-100 rounded-lg text-violet-600 group-hover:scale-110 transition-transform duration-300">
                      <WineGlassIcon />
                    </div>
                    <div className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-1 rounded-full">In Progress</div>
                  </div>
                  <p className="text-sm font-medium text-monday-text-muted mb-1">Active Batches</p>
                  <p className="text-3xl font-bold text-monday-text mb-2 group-hover:text-violet-600 transition-colors">{batches.length}</p>
                  <div className="flex gap-3 text-xs text-monday-text-muted">
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400"></span>
                      {activeFermentations} fermenting
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                      {activeAgingProcesses} aging
                    </span>
                  </div>
                </div>
              </Link>

              {/* Vessel Utilization */}
              <Link to="/vessels" className="group relative bg-gradient-to-br from-white to-info-50/40 border border-monday-border-light rounded-xl p-5 hover:border-info-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/0 to-blue-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2.5 bg-info-50 rounded-lg text-info-600 group-hover:scale-110 transition-transform duration-300">
                      <WarehouseIcon />
                    </div>
                    <div className="text-xs font-medium text-info-600 bg-info-50 px-2 py-1 rounded-full">{vesselUtilizationPercent}%</div>
                  </div>
                  <p className="text-sm font-medium text-monday-text-muted mb-1">Vessel Usage</p>
                  <p className="text-3xl font-bold text-monday-text mb-2 group-hover:text-info-600 transition-colors">{vessels.length}</p>
                  <div className="w-full bg-monday-header rounded-full h-2 mb-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-info-500 to-primary-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${vesselUtilizationPercent}%` }}
                    ></div>
                  </div>
                  <div className="flex gap-3 text-xs text-monday-text-muted">
                    <span>{inUseVessels} in use</span>
                    <span>•</span>
                    <span>{availableVessels} available</span>
                  </div>
                </div>
              </Link>

              {/* Bottling Stats */}
              <Link to="/bottling" className="group relative bg-gradient-to-br from-white to-secondary-50/50 border border-monday-border-light rounded-xl p-5 hover:border-secondary-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-400/0 to-rose-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2.5 bg-rose-100 rounded-lg text-rose-600 group-hover:scale-110 transition-transform duration-300">
                      <WineBottleIcon />
                    </div>
                    <div className="text-xs font-medium text-rose-600 bg-rose-50 px-2 py-1 rounded-full">{plannedBottlings} runs</div>
                  </div>
                  <p className="text-sm font-medium text-monday-text-muted mb-1">Total Bottles</p>
                  <p className="text-3xl font-bold text-monday-text mb-2 group-hover:text-rose-600 transition-colors">{totalBottles.toLocaleString()}</p>
                  <p className="text-sm text-monday-text-muted">
                    {plannedBottlings} bottling runs planned
                  </p>
                </div>
              </Link>
            </div>

            {/* Production Pipeline Visual */}
            <div className="bg-white/90 border border-monday-border-light rounded-2xl p-6 md:p-8 mb-8 shadow">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-1 h-6 bg-gradient-to-b from-secondary-500 via-primary-500 to-info-500 rounded-full"></div>
                <h3 className="text-lg font-semibold text-monday-text">Production Pipeline</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Link to="/fermentation" className="group block bg-white border border-secondary-200 rounded-xl p-5 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-2 bg-orange-100 rounded-lg text-orange-600 group-hover:scale-110 transition-transform duration-300">
                        <FlaskIcon />
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-2xl font-bold text-monday-text group-hover:text-orange-600 transition-colors">{activeFermentations}</span>
                        <span className="text-xs text-orange-600 font-medium">active</span>
                      </div>
                    </div>
                    <h4 className="font-semibold text-monday-text mb-1 text-base">Fermentation</h4>
                    <p className="text-sm text-monday-text-muted mb-4">Active fermentation processes</p>
                    <div className="flex items-center text-sm font-medium text-orange-600 group-hover:translate-x-1 transition-transform">
                      View Details
                      <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                  <div className="hidden md:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10 items-center">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <Link to="/aging" className="group block bg-white border border-warning-200 rounded-xl p-5 hover:border-warning-400 hover:shadow-md transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-2 bg-amber-100 rounded-lg text-amber-600 group-hover:scale-110 transition-transform duration-300">
                        <ClockIcon />
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-2xl font-bold text-monday-text group-hover:text-amber-600 transition-colors">{activeAgingProcesses}</span>
                        <span className="text-xs text-amber-600 font-medium">active</span>
                      </div>
                    </div>
                    <h4 className="font-semibold text-monday-text mb-1 text-base">Aging</h4>
                    <p className="text-sm text-monday-text-muted mb-4">Wines maturing in barrels</p>
                    <div className="flex items-center text-sm font-medium text-amber-600 group-hover:translate-x-1 transition-transform">
                      View Details
                      <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                  <div className="hidden md:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10 items-center">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-amber-400 to-blue-400 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <Link to="/bottling" className="group block bg-white border border-info-200 rounded-xl p-5 hover:border-info-400 hover:shadow-md transition-all duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2 bg-info-50 rounded-lg text-info-600 group-hover:scale-110 transition-transform duration-300">
                      <WineBottleIcon />
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-2xl font-bold text-monday-text group-hover:text-info-600 transition-colors">{plannedBottlings}</span>
                      <span className="text-xs text-info-600 font-medium">planned</span>
                    </div>
                  </div>
                  <h4 className="font-semibold text-monday-text mb-1 text-base">Bottling</h4>
                  <p className="text-sm text-monday-text-muted mb-4">Scheduled bottling runs</p>
                  <div className="flex items-center text-sm font-medium text-info-600 group-hover:translate-x-1 transition-transform">
                    View Schedule
                    <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </div>
            </div>

            {/* Charts Section - Mobile Responsive Layout */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
              {/* Wine Type Distribution */}
              <div className="group bg-white border border-monday-border-light rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-monday-border-light">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-5 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                    <h3 className="text-base font-semibold text-monday-text">Wine Portfolio</h3>
                  </div>
                  <p className="text-sm text-monday-text-muted mt-1">Distribution by wine type</p>
                </div>
                <div className="p-6 bg-gradient-to-br from-white to-slate-50/30">
                  <ResponsiveChart
                    type="doughnut"
                    data={wineTypeChartData}
                    title=""
                    height="300px"
                    variant="embedded"
                  />
                </div>
              </div>

              {/* Production Timeline */}
              <div className="group bg-white border border-monday-border-light rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-monday-border-light">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full"></div>
                    <h3 className="text-base font-semibold text-monday-text">Production Status</h3>
                  </div>
                  <p className="text-sm text-monday-text-muted mt-1">Active processes by stage</p>
                </div>
                <div className="p-6 bg-gradient-to-br from-white to-slate-50/30">
                  <ResponsiveChart
                    type="bar"
                    data={productionChartData}
                    title=""
                    height="300px"
                    variant="embedded"
                  />
                </div>
              </div>
            </div>

            {/* Recent Activity Tables */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Active Wine Batches */}
              <div className="group bg-white border border-monday-border-light rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
                <div className="px-6 py-4 bg-gradient-to-r from-violet-50/50 to-white border-b border-monday-border-light flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-4 bg-violet-500 rounded-full"></div>
                      <h3 className="text-base font-semibold text-monday-text">Recent Batches</h3>
                    </div>
                    <p className="text-sm text-monday-text-muted">Latest wine batches</p>
                  </div>
                  <Link
                    to="/batches"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-violet-700 hover:text-violet-900 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
                  >
                    View All
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
                <ResponsiveTable
                  columns={batchColumns}
                  data={batches.slice(0, 5)}
                  emptyMessage="No active wine batches found"
                  title="Recent Batches"
                  subtitle="Latest wine batches"
                  enableExport
                  exportFileName={`dashboard-batches-${new Date().toISOString().slice(0, 10)}.xlsx`}
                />
              </div>

              {/* Upcoming Bottling */}
              <div className="group bg-white border border-monday-border-light rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-50/50 to-white border-b border-monday-border-light flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                      <h3 className="text-base font-semibold text-monday-text">Upcoming Bottling</h3>
                    </div>
                    <p className="text-sm text-monday-text-muted">Scheduled runs</p>
                  </div>
                  <Link
                    to="/bottling"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-info-700 hover:text-info-900 bg-info-50 hover:bg-info-100 rounded-full transition-colors"
                  >
                    View All
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
                <ResponsiveTable
                  columns={bottlingColumns}
                  data={upcomingBottlingRuns}
                  emptyMessage="No upcoming bottling runs scheduled"
                  title="Upcoming Bottling"
                  subtitle="Scheduled bottling runs"
                  enableExport
                  exportFileName={`dashboard-bottling-${new Date().toISOString().slice(0, 10)}.xlsx`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Production Tab Content */}
        {activeTab === 'production' && (
          <div>
            {/* Production Stage Details */}
            <div className="grid grid-cols-1 gap-6 mb-6">
              {/* Fermentation Details */}
              <div className="bg-white border border-monday-border-light rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-monday-border-light">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-monday-text-muted">
                        <FlaskIcon />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-monday-text">Fermentation</h3>
                        <p className="text-sm text-monday-text-muted">{activeFermentations} active processes</p>
                      </div>
                    </div>
                    <Link
                      to="/fermentation"
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-monday-text hover:text-monday-text border border-monday-border rounded-md hover:border-monday-border transition-colors"
                    >
                      Manage
                    </Link>
                  </div>
                </div>
                <ResponsiveTable
                  columns={fermentationColumns}
                  data={fermentations.filter(f => f.status === 'Active')}
                  emptyMessage="No active fermentation processes"
                  title="Active Fermentations"
                  subtitle="Current fermentation processes"
                  enableExport
                  exportFileName={`dashboard-fermentation-${new Date().toISOString().slice(0, 10)}.xlsx`}
                />
              </div>

              {/* Aging Details */}
              <div className="bg-white border border-monday-border-light rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-monday-border-light">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-monday-text-muted">
                        <ClockIcon />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-monday-text">Aging</h3>
                        <p className="text-sm text-monday-text-muted">{activeAgingProcesses} active processes</p>
                      </div>
                    </div>
                    <Link
                      to="/aging"
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-monday-text hover:text-monday-text border border-monday-border rounded-md hover:border-monday-border transition-colors"
                    >
                      Manage
                    </Link>
                  </div>
                </div>
                <ResponsiveTable
                  columns={fermentationColumns}
                  data={agingProcesses.filter(a => a.status === 'Active')}
                  emptyMessage="No active aging processes"
                  title="Active Aging"
                  subtitle="Current aging processes"
                  enableExport
                  exportFileName={`dashboard-aging-${new Date().toISOString().slice(0, 10)}.xlsx`}
                />
              </div>

              {/* Bottling Schedule */}
              <div className="bg-white border border-monday-border-light rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-monday-border-light">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-monday-text-muted">
                        <WineBottleIcon />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-monday-text">Bottling Schedule</h3>
                        <p className="text-sm text-monday-text-muted">{plannedBottlings} upcoming runs</p>
                      </div>
                    </div>
                    <Link
                      to="/bottling"
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-monday-text hover:text-monday-text border border-monday-border rounded-md hover:border-monday-border transition-colors"
                    >
                      Manage
                    </Link>
                  </div>
                </div>
                <ResponsiveTable
                  columns={bottlingColumns}
                  data={upcomingBottlingRuns}
                  emptyMessage="No upcoming bottling runs"
                  title="Upcoming Bottling"
                  subtitle="Scheduled bottling runs"
                  enableExport
                  exportFileName={`dashboard-bottling-${new Date().toISOString().slice(0, 10)}.xlsx`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Quality & Alerts Tab Content */}
        {activeTab === 'quality' && (
          <div>
            {/* Alerts & Notifications */}
            <div className="grid grid-cols-1 gap-4 mb-6">
              {/* Urgent Alerts */}
              {upcomingBottlingRuns.length > 0 && upcomingBottlingRuns.some(run => {
                const dateStr = run.bottlingDate || run.bottling_date;
                if (!dateStr) return false;
                const daysUntil = differenceInDays(parseISO(dateStr), new Date());
                return daysUntil <= 7 && daysUntil >= 0;
              }) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-amber-900">Upcoming Bottling Alert</h3>
                        <p className="mt-1 text-sm text-amber-800">
                          You have bottling runs scheduled within the next 7 days. Please ensure vessels and equipment are prepared.
                        </p>
                        <div className="mt-3">
                          <Link
                            to="/bottling"
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-900 hover:text-amber-950 border border-amber-300 rounded-md hover:border-amber-400 transition-colors"
                          >
                            View Schedule
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Capacity Alerts */}
              {vesselUtilizationPercent > 80 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-900">High Vessel Utilization</h3>
                      <p className="mt-1 text-sm text-red-800">
                        Your vessel utilization is at {vesselUtilizationPercent}%. Consider planning capacity for new batches or completing aging processes.
                      </p>
                      <div className="mt-3">
                        <Link
                          to="/vessels"
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-900 hover:text-red-950 border border-red-300 rounded-md hover:border-red-400 transition-colors"
                        >
                          Manage Vessels
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Production Health */}
              <div className="bg-white border border-monday-border-light rounded-lg p-6">
                <h3 className="text-base font-semibold text-monday-text mb-6">Production Health</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-monday-header border border-monday-border-light rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="h-5 w-5 text-monday-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-2xl font-semibold text-monday-text">{batches.length}</span>
                    </div>
                    <h4 className="font-medium text-monday-text text-sm">Active Batches</h4>
                    <p className="text-xs text-monday-text-muted mt-1">All stages of production</p>
                  </div>

                  <div className="bg-monday-header border border-monday-border-light rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="h-5 w-5 text-monday-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="text-2xl font-semibold text-monday-text">{vesselUtilizationPercent}%</span>
                    </div>
                    <h4 className="font-medium text-monday-text text-sm">Capacity Used</h4>
                    <p className="text-xs text-monday-text-muted mt-1">{availableVessels} vessels available</p>
                  </div>

                  <div className="bg-monday-header border border-monday-border-light rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="h-5 w-5 text-monday-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-2xl font-semibold text-monday-text">{vineyards.length}</span>
                    </div>
                    <h4 className="font-medium text-monday-text text-sm">Vineyards</h4>
                    <p className="text-xs text-monday-text-muted mt-1">{totalAcreage.toFixed(1)} acres total</p>
                  </div>
                </div>
              </div>

              {/* Vessel Utilization Details */}
              <div className="bg-white border border-monday-border-light rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-monday-border-light">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-monday-text">Vessel Status</h3>
                      <p className="text-sm text-monday-text-muted mt-0.5">Current vessel inventory and availability</p>
                    </div>
                    <Link
                      to="/vessels"
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-monday-text hover:text-monday-text border border-monday-border rounded-md hover:border-monday-border transition-colors"
                    >
                      Manage Vessels
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-monday-header rounded-md p-3 border border-monday-border-light">
                      <div className="text-xl font-semibold text-monday-text">{vessels.length}</div>
                      <div className="text-xs text-monday-text-muted mt-0.5">Total</div>
                    </div>
                    <div className="bg-monday-header rounded-md p-3 border border-monday-border-light">
                      <div className="text-xl font-semibold text-monday-text">{availableVessels}</div>
                      <div className="text-xs text-monday-text-muted mt-0.5">Available</div>
                    </div>
                    <div className="bg-monday-header rounded-md p-3 border border-monday-border-light">
                      <div className="text-xl font-semibold text-monday-text">{inUseVessels}</div>
                      <div className="text-xs text-monday-text-muted mt-0.5">In Use</div>
                    </div>
                    <div className="bg-monday-header rounded-md p-3 border border-monday-border-light">
                      <div className="text-xl font-semibold text-monday-text">{vesselUtilizationPercent}%</div>
                      <div className="text-xs text-monday-text-muted mt-0.5">Utilized</div>
                    </div>
                  </div>
                </div>
                <ResponsiveTable
                  columns={vesselColumns}
                  data={vessels}
                  emptyMessage="No vessels found"
                  linkTo={(row) => `/vessels/${row.id}`}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Floating Action Button */}
      <div className="md:hidden">
        <MobileFAB
          actions={fabActions}
          mainAction={mainFabAction}
        />
      </div>
    </div>
  );
};

export default Dashboard;
